import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Slider,
    Chip,
    Divider,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
    FirstPage as FirstPageIcon,
    LastPage as LastPageIcon,
    RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { transformToBTC } from '../../utils/helpers';

// Helper to shorten long ID strings
const shortenLabel = (label, startChars = 6, endChars = 6) => {
    if (typeof label !== 'string' || label.length <= startChars + endChars + 3) {
        return label;
    }
    return `${ label.substring(0, startChars) }...${ label.substring(label.length - endChars) }`;
};

const D3NetworkGraph = ({
    userId,
    graphData,
    loading,
    error,
    onRefresh,
    onNodeClick
}) => {
    const { t } = useTranslation();
    const svgRef = useRef();
    const zoomBehavior = useRef();
    const simulationRef = useRef();
    const pinnedPositions = useRef(new Map());

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [windowSize, setWindowSize] = useState(50);
    const [neighborPageSize, setNeighborPageSize] = useState(50);
    const [currentWindow, setCurrentWindow] = useState(0);
    const [selectedNodeTypes, setSelectedNodeTypes] = useState(new Set(['user', 'transaction', 'address']));
    const [showCumulative, setShowCumulative] = useState(true);
    const [highlightedNodeIds, setHighlightedNodeIds] = useState(new Set());
    const [highlightedLinkIds, setHighlightedLinkIds] = useState(new Set());

    const nodeColors = { user: '#81C784', transaction: '#c97929ff', address: '#64B5F6' };

    // FIX: Process the incoming graphData prop to ensure links have unique IDs.
    const processedGraphData = useMemo(() => {
        if (!graphData || !graphData.links) return { nodes: [], links: [] };
        // Ensure every link has a unique ID for D3's data join.
        const linksWithId = graphData.links.map((link, idx) => ({
            ...link,
            id: link.id || `link-${ link.source?.id || link.source }-${ link.target?.id || link.target }-${ idx }`
        }));
        return { ...graphData, links: linksWithId };
    }, [graphData]);


    const explorationPlan = useMemo(() => {
        if (!processedGraphData?.nodes?.length) return [];
        const plan = [];
        const filteredNodes = processedGraphData.nodes.filter(node => selectedNodeTypes.has(node.type));
        const neighborsOf = new Map();
        processedGraphData.nodes.forEach(n => neighborsOf.set(n.id, []));
        processedGraphData.links.forEach(link => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            neighborsOf.get(sourceId)?.push(targetId);
            neighborsOf.get(targetId)?.push(sourceId);
        });

        const processedAsHub = new Set();
        for (const node of filteredNodes) {
            const neighbors = Array.from(new Set(neighborsOf.get(node.id) || []));
            if (neighbors.length > neighborPageSize) {
                processedAsHub.add(node.id);
                for (let i = 0; i < neighbors.length; i += neighborPageSize) {
                    const neighborSlice = neighbors.slice(i, i + neighborPageSize);
                    const nodeIdsInView = new Set([node.id, ...neighborSlice]);
                    plan.push({ title: `${ shortenLabel(node.label || node.id) } (Neighbors ${ i + 1 }-${ i + neighborSlice.length })`, nodeIds: nodeIdsInView });
                }
            }
        }
        const remainingNodes = filteredNodes.filter(n => !processedAsHub.has(n.id));
        for (let i = 0; i < remainingNodes.length; i += windowSize) {
            const nodeSlice = remainingNodes.slice(i, i + windowSize);
            const nodeIdsInView = new Set(nodeSlice.map(n => n.id));
            nodeSlice.forEach(n => { (neighborsOf.get(n.id) || []).forEach(neighborId => nodeIdsInView.add(neighborId)); });
            const endRange = Math.min(i + windowSize, remainingNodes.length);
            plan.push({ title: `Nodes ${ i + 1 }-${ endRange } & Their Neighbors`, nodeIds: nodeIdsInView });
        }
        return plan;
    }, [processedGraphData, selectedNodeTypes, windowSize, neighborPageSize]);

    const windowedData = useMemo(() => {
        const totalWindows = explorationPlan.length;
        if (totalWindows === 0 || !processedGraphData?.nodes) return { currentNodes: [], currentLinks: [], totalWindows: 0, title: t('noData'), stats: { total: 0, showing: 0, window: 0, links: 0 } };

        const currentPlanItem = explorationPlan[Math.min(currentWindow, totalWindows - 1)];
        const { nodeIds, title } = currentPlanItem;
        const allNodesById = new Map(processedGraphData.nodes.map(node => [node.id, node]));
        const currentNodes = Array.from(nodeIds).map(id => allNodesById.get(id)).filter(Boolean);
        const currentLinks = processedGraphData.links.filter(link => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            return nodeIds.has(sourceId) && nodeIds.has(targetId);
        });
        const totalFilteredNodes = processedGraphData.nodes.filter(node => selectedNodeTypes.has(node.type)).length;
        return { currentNodes, currentLinks, totalWindows, title, stats: { total: totalFilteredNodes, showing: currentNodes.length, window: currentWindow + 1, links: currentLinks.length } };
    }, [processedGraphData, explorationPlan, currentWindow, selectedNodeTypes]);

    const linksToRender = useMemo(() => {
        const { currentLinks } = windowedData;
        if (showCumulative) {
            const cumulativeMap = new Map();
            currentLinks.forEach(link => {
                const sourceId = link.source.id || link.source;
                const targetId = link.target.id || link.target;
                const key = `${ sourceId }-${ targetId }`;
                const amount = link.amount || 0;
                if (cumulativeMap.has(key)) {
                    cumulativeMap.get(key).totalAmount += amount;
                } else {
                    cumulativeMap.set(key, { source: link.source, target: link.target, totalAmount: amount, id: `cumulative-${ key }` });
                }
            });
            return Array.from(cumulativeMap.values());
        } else {
            const linkGroups = d3.group(currentLinks, d => [d.source.id || d.source, d.target.id || d.target].sort().join('-'));
            linkGroups.forEach(group => {
                const groupSize = group.length;
                if (groupSize > 1) {
                    group.forEach((link, i) => { link.arc = (i - (groupSize - 1) / 2); });
                } else {
                    group[0].arc = 0;
                }
            });
            return currentLinks;
        }
    }, [windowedData.currentLinks, showCumulative]);


    const navigateWindow = useCallback((direction) => {
        setCurrentWindow(prev => {
            switch (direction) { case 'first': return 0; case 'prev': return Math.max(0, prev - 1); case 'next': return Math.min(windowedData.totalWindows - 1, prev + 1); case 'last': return windowedData.totalWindows - 1; default: return prev; }
        });
    }, [windowedData.totalWindows]);

    useEffect(() => { setCurrentWindow(0); }, [processedGraphData, windowSize, neighborPageSize, selectedNodeTypes]);
    const toggleNodeType = useCallback((type) => { setSelectedNodeTypes(prev => { const newSet = new Set(prev); if (newSet.has(type)) newSet.delete(type); else newSet.add(type); return newSet; }); }, []);
    const resetLayout = () => { pinnedPositions.current.clear(); windowedData.currentNodes.forEach(node => { node.fx = null; node.fy = null; }); if (simulationRef.current) simulationRef.current.alpha(1).restart(); };
    useEffect(() => { const handleResize = () => { const container = svgRef.current?.parentElement; if (container) setDimensions({ width: container.clientWidth, height: container.clientHeight }); }; handleResize(); window.addEventListener('resize', handleResize); return () => window.removeEventListener('resize', handleResize); }, []);

    useEffect(() => {
        if (!windowedData.currentNodes.length) { d3.select(svgRef.current).selectAll('.main-container').remove(); return; };

        const svg = d3.select(svgRef.current);
        if (!svgRef.current) return; // Add null check
        const { width, height } = svgRef.current.getBoundingClientRect();

        let container = svg.select('.main-container');
        if (container.empty()) {
            svg.selectAll('*').remove();
            container = svg.append('g').attr('class', 'main-container');
            zoomBehavior.current = d3.zoom().scaleExtent([0.1, 4]).on('zoom', (event) => container.attr('transform', event.transform));
            svg.call(zoomBehavior.current);
            container.append('g').attr('class', 'links');
            container.append('g').attr('class', 'nodes');
            const defs = container.append('defs');
            Object.entries(nodeColors).forEach(([type, color]) => {
                const grad = defs.append('radialGradient').attr('id', `gradient-${ type }`).attr('cx', '30%').attr('cy', '30%');
                grad.append('stop').attr('offset', '0%').attr('stop-color', d3.color(color).brighter(0.5));
                grad.append('stop').attr('offset', '100%').attr('stop-color', color);
            });
        }

        let defs = container.select('defs');
        defs.selectAll('marker').data([{ id: 'arrowhead-default', color: '#888' }, { id: 'arrowhead-highlight', color: '#FFD700' }]).join(enter => enter.append('marker').attr('id', d => d.id).attr('viewBox', '-0 -5 10 10').attr('refX', 23).attr('refY', 0).attr('orient', 'auto').attr('markerWidth', 6).attr('markerHeight', 6).append('path').attr('d', 'M0,-5L10,0L0,5')).select('path').attr('fill', d => d.color);

        const g = container;
        const { currentNodes } = windowedData;

        currentNodes.forEach(node => { if (pinnedPositions.current.has(node.id)) { const pos = pinnedPositions.current.get(node.id); node.fx = pos.fx; node.fy = pos.fy; } });

        if (simulationRef.current) simulationRef.current.stop();

        const simulation = d3.forceSimulation(currentNodes)
            .force('link', d3.forceLink(linksToRender).id(d => d.id).distance(120).strength(0.1))
            .force('charge', d3.forceManyBody().strength(-300).distanceMax(400))
            .force('center', d3.forceCenter(width / 2, height / 2))
            .force('collision', d3.forceCollide().radius(d => getNodeRadius(d.type) + 8).strength(0.8));
        simulationRef.current = simulation;

        function getNodeRadius(type) { return ({ user: 22, transaction: 18, address: 16 })[type] || 14; }

        const linkGroup = g.select('.links').selectAll('.link-group').data(linksToRender, d => d.id);
        linkGroup.exit().remove();
        const linkGroupEnter = linkGroup.enter().append('g').attr('class', 'link-group');
        linkGroupEnter.append('path').attr('class', 'link-line').attr('fill', 'none');
        linkGroupEnter.append('text').attr('class', 'link-label').attr('font-size', '10px').attr('font-weight', '500').attr('text-anchor', 'middle').attr('dy', -5).style('pointer-events', 'none').style('paint-order', 'stroke fill').style('stroke', '#1A1E2E').style('stroke-width', '3px').style('stroke-linejoin', 'round');

        const mergedLinkGroup = linkGroupEnter.merge(linkGroup)
            .attr('opacity', d => highlightedLinkIds.has(d.id) ? 1 : (highlightedNodeIds.size > 0 ? 0.2 : 1));

        mergedLinkGroup.select('.link-line')
            .attr('stroke', d => highlightedLinkIds.has(d.id) ? '#FFD700' : '#888')
            .attr('stroke-width', d => highlightedLinkIds.has(d.id) ? 2.5 : 1.5)
            .attr('marker-end', d => highlightedLinkIds.has(d.id) ? 'url(#arrowhead-highlight)' : 'url(#arrowhead-default)');

        mergedLinkGroup.select('.link-label')
            .attr('fill', d => highlightedLinkIds.has(d.id) ? '#FFD700' : '#FFFFFF')
            .text(d => {
                const amount = d.totalAmount || d.amount;
                return amount ? transformToBTC(amount) : '';
            });

        const node = g.select('.nodes').selectAll('g.node').data(currentNodes, d => d.id);
        node.exit().remove();
        const nodeEnter = node.enter().append('g').attr('class', 'node').style('cursor', 'pointer');
        nodeEnter.append('circle').attr('stroke-width', 2);
        nodeEnter.append('text').attr('class', 'node-label').attr('text-anchor', 'middle').attr('fill', '#FFFFFF').attr('font-size', '10px');
        const mergedNode = nodeEnter.merge(node)
            .attr('opacity', d => highlightedNodeIds.size > 0 && !highlightedNodeIds.has(d.id) ? 0.2 : 1);

        mergedNode.select('circle').attr('r', d => getNodeRadius(d.type)).attr('fill', d => `url(#gradient-${ d.type })`).attr('stroke', d => highlightedNodeIds.has(d.id) ? '#FFD700' : (nodeColors[d.type] || '#ccc')).attr('stroke-width', d => highlightedNodeIds.has(d.id) ? 3 : 2);
        mergedNode.select('text').attr('y', d => getNodeRadius(d.type) + 14).text(d => shortenLabel(d.label || d.id));

        mergedNode.on('click', (event, d) => { event.stopPropagation(); onNodeClick(d); })
            .on('mouseover', function (event, d) {
                const newHighlightedNodes = new Set([d.id]);
                const newHighlightedLinks = new Set();
                linksToRender.forEach(l => { if ((l.source.id || l.source) === d.id || (l.target.id || l.target) === d.id) { newHighlightedLinks.add(l.id); newHighlightedNodes.add(l.source.id || l.source); newHighlightedNodes.add(l.target.id || l.target); } });
                setHighlightedNodeIds(newHighlightedNodes);
                setHighlightedLinkIds(newHighlightedLinks);
            })
            .on('mouseout', function () { setHighlightedNodeIds(new Set()); setHighlightedLinkIds(new Set()); });
        mergedNode.call(d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended));

        simulation.on('tick', () => {
            mergedLinkGroup.select('.link-line').attr('d', d => {
                const dx = d.target.x - d.source.x, dy = d.target.y - d.source.y;
                if (d.arc === 0 || !d.arc) return `M${ d.source.x },${ d.source.y }L${ d.target.x },${ d.target.y }`;
                const midX = (d.source.x + d.target.x) / 2, midY = (d.source.y + d.target.y) / 2;
                const length = Math.sqrt(dx * dx + dy * dy);
                if (length === 0) return `M${ d.source.x },${ d.source.y }`;
                const offsetX = -dy / length * d.arc * 20, offsetY = dx / length * d.arc * 20;
                return `M${ d.source.x },${ d.source.y }Q${ midX + offsetX },${ midY + offsetY } ${ d.target.x },${ d.target.y }`;
            });
            mergedLinkGroup.select('.link-label').attr('transform', d => {
                if (d.arc === 0 || !d.arc) return `translate(${ (d.source.x + d.target.x) / 2 }, ${ (d.source.y + d.target.y) / 2 })`;
                const dx = d.target.x - d.source.x, dy = d.target.y - d.source.y;
                const midX = (d.source.x + d.target.x) / 2, midY = (d.source.y + d.target.y) / 2;
                const length = Math.sqrt(dx * dx + dy * dy);
                if (length === 0) return `translate(${ midX }, ${ midY })`;
                const offsetX = -dy / length * d.arc * 20, offsetY = dx / length * d.arc * 20;
                return `translate(${ midX + offsetX }, ${ midY + offsetY })`;
            });
            mergedNode.attr('transform', d => `translate(${ d.x }, ${ d.y })`);
        });

        function dragstarted(event, d) { event.sourceEvent.stopPropagation(); if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; }
        function dragged(event, d) { d.fx = event.x; d.fy = event.y; }
        function dragended(event, d) { if (!event.active) simulation.alphaTarget(0); pinnedPositions.current.set(d.id, { fx: d.x, fy: d.y }); }

        return () => { if (simulationRef.current) simulationRef.current.stop(); };
    }, [windowedData, linksToRender, onNodeClick, highlightedNodeIds, highlightedLinkIds]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}><CircularProgress /><Typography sx={{ ml: 2 }}>{t('loadingNetwork')}</Typography></Box>;
    if (error) return <Alert severity="error" action={<Button onClick={onRefresh} size="small">{t('retry')}</Button>}>{error}</Alert>;
    if (!processedGraphData?.nodes?.length) return <Paper sx={{ p: 3, textAlign: 'center' }}><Typography>{t('noNetworkConnections')}</Typography></Paper>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', bgcolor: 'background.default' }}>
            <Box sx={{ border: '1px solid #444', borderRadius: '8px', height: 650, width: '100%', position: 'relative', overflow: 'hidden', bgcolor: '#1A1E2E' }}>
                <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={resetLayout} startIcon={<RestartAltIcon />} sx={{ bgcolor: 'rgba(26, 30, 46, 0.9)' }}>{t('resetLayout')}</Button>
                    <Button variant="outlined" size="small" onClick={onRefresh} startIcon={<RefreshIcon />} sx={{ bgcolor: 'rgba(26, 30, 46, 0.9)' }}>{t('refresh')}</Button>
                </Box>
            </Box>

            <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, mt: 1, boxShadow: 1 }}>
                <IconButton size="small" onClick={() => navigateWindow('first')} disabled={currentWindow === 0}><FirstPageIcon /></IconButton>
                <IconButton size="small" onClick={() => navigateWindow('prev')} disabled={currentWindow === 0}><NavigateBeforeIcon /></IconButton>
                <Box sx={{ minWidth: 200, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{windowedData.title}</Typography>
                    <Typography variant="caption" display="block">Win: {windowedData.stats.window || 1}/{windowedData.totalWindows || 1}</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigateWindow('next')} disabled={currentWindow >= windowedData.totalWindows - 1}><NavigateNextIcon /></IconButton>
                <IconButton size="small" onClick={() => navigateWindow('last')} disabled={currentWindow >= windowedData.totalWindows - 1}><LastPageIcon /></IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                {/* <Box sx={{ display: 'flex', gap: 1 }}>
                    {['user', 'transaction', 'address'].map(type => (<Chip key={type} label={t(type)} size="small" variant={selectedNodeTypes.has(type) ? 'filled' : 'outlined'} onClick={() => toggleNodeType(type)} sx={{ bgcolor: selectedNodeTypes.has(type) ? nodeColors[type] : 'transparent', color: selectedNodeTypes.has(type) ? 'white' : 'text.primary' }} />))}
                </Box> */}
                <Box sx={{ display: 'flex', gap: 1 }}>{['user', 'address'].map(type => (<Chip key={type} label={type === 'user' ? t('user.user') : t('explorer.address')} size="small" variant={selectedNodeTypes.has(type) ? 'filled' : 'outlined'} onClick={() => toggleNodeType(type)} sx={{ bgcolor: selectedNodeTypes.has(type) ? nodeColors[type] : 'transparent', color: selectedNodeTypes.has(type) ? 'white' : 'text.primary' }} />))}</Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography variant="body2" color="text.secondary">Nodes: {windowedData.stats.showing}/{windowedData.stats.total}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Links: {linksToRender.length}</Typography>
                <Box sx={{ flex: 1 }} />
                <FormControlLabel control={<Switch checked={showCumulative} onChange={(e) => setShowCumulative(e.target.checked)} />} label={t('cumulativeLinks')} sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Hub Size:</Typography>
                    <Slider value={neighborPageSize} onChange={(_, value) => setNeighborPageSize(value)} min={10} max={200} step={10} size="small" sx={{ width: 80 }} valueLabelDisplay="auto" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Grp. Size:</Typography>
                    <Slider value={windowSize} onChange={(_, value) => setWindowSize(value)} min={10} max={200} step={10} size="small" sx={{ width: 80 }} valueLabelDisplay="auto" />
                </Box>
            </Paper>
        </Box>
    );
};

export default D3NetworkGraph;